import React, { useEffect, useRef, useState } from 'react';

import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Flex } from 'antd';
import { kebabCase } from 'lodash';

interface IEditableCardProps {
  title: string;
  summary: React.ReactNode;
  children: React.ReactNode;
  defaultEditing?: boolean;
  disableDone?: boolean;
  onEditingChange?: (editing: boolean) => void;
  onEditStart?: () => void;
  onCancel?: () => void;
}

export const EditableCard = ({
  title,
  summary,
  children,
  defaultEditing = false,
  disableDone = false,
  onEditingChange,
  onEditStart,
  onCancel,
}: IEditableCardProps) => {
  const [editing, setEditing] = useState(defaultEditing);
  const editingRef = useRef(editing);
  const onEditingChangeRef = useRef(onEditingChange);
  const onEditStartRef = useRef(onEditStart);
  const onCancelRef = useRef(onCancel);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  useEffect(() => {
    onEditingChangeRef.current = onEditingChange;
  }, [onEditingChange]);

  useEffect(() => {
    onEditStartRef.current = onEditStart;
  }, [onEditStart]);

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    if (defaultEditing) {
      setEditing(true);
    }
  }, [defaultEditing]);

  useEffect(() => {
    if (editing) {
      onEditingChangeRef.current?.(true);
      onEditStartRef.current?.();
    } else {
      onEditingChangeRef.current?.(false);
    }
  }, [editing]);

  useEffect(
    () => () => {
      if (editingRef.current && onEditingChangeRef.current) {
        onEditingChangeRef.current(false);
      }
    },
    // Reason: Cleanup should only notify parent on unmount if card was in editing mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleCancel = () => {
    onCancelRef.current?.();
    setEditing(false);
  };

  const handleDone = () => {
    setEditing(false);
  };

  const handleEdit = () => {
    setEditing(true);
  };

  return (
    <Card
      size="small"
      title={title}
      extra={
        editing ? (
          <Flex gap="small">
            <Button
              type="link"
              size="small"
              danger
              data-testid={`cancel-section-${kebabCase(title)}`}
              icon={<CloseOutlined />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="link"
              size="small"
              disabled={disableDone}
              data-testid={`done-section-${kebabCase(title)}`}
              icon={<CheckOutlined />}
              onClick={handleDone}
            >
              Done
            </Button>
          </Flex>
        ) : (
          <Button
            type="link"
            size="small"
            data-testid={`edit-section-${kebabCase(title)}`}
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )
      }
    >
      {editing ? children : summary}
    </Card>
  );
};
